import React, { useMemo, useState } from 'react';
import UploadFile from '../setup-assistant/upload-document/upload-file';
import classes from './configuration-from-pdf.module.scss';

import InfoHint from '../info-hint/info-hint-view';
import NumberFormat from 'react-number-format';
import { useForceUpdate } from '../../hooks/use-force-update';
import {
  BASKET_TYPES,
  CONFIGURATION_TYPES,
  crmDataTypes,
  MAX_DISTANCE,
  MIN_DISTANCE,
  uds_enginetypecode,
  USER_ROLES,
  VEHICLE_OFFER_ASSIGNMENT
} from '../../constants';
import { CircleExclamationMark } from '../icons/crcle_exclamation_mark';
import { useLocation } from 'react-router-dom';
import MonthsComponent, { MonthsData } from './months-component/months-component';
import DeliveryDateComponent, { DeliveryDateData } from './delivery-date-component/delivery-date-component';
import { Button, Divider, Flex, Text } from '../../common/common-components.styled';
import Switch from 'react-switch';
import { useTypedSelector } from '../../hooks/use-typed-selector';
import { CrmDataTypes, PAGES } from '../../common/enums';
import { useIsUserHaveRole } from '../../hooks/use-is-user-have-role';
import useDriverFields from './hooks/use-driver-fields';
import { CloseIcon } from '../icons-new/close';
import IconHover from '../icon-hover';
import {
  ActionType,
  ConfigurationFromPdfFieldNames,
  ConfigurationFromPdfProps,
  ControlledInputProps,
  CustomInputProps,
  IDistance,
  MonthsControlProps,
  SaveTypes
} from './configuration-from-pdf.types';
import { AnyObject, User } from '../../common/interfaces';
import { StyledConfigurationFromPdf, StyledLeftContainer, StyledRightContainer } from './configuration-from-pdf.styled';
import { InfoHintType } from '../info-hint/info-hint.props';
import { useForm } from 'react-hook-form';
import FormHook from '../form-hook/form-hook';
import { generateFormField } from '../../utils/form/generate-form-fields/generate-form-fields';
import { errorMessages } from '../../utils/error-messages';
import { FormSourceEnum } from '../form-hook/form-field/hooks/use-set-options/use-set-options.props';
import useOldCarFields from './hooks/use-old-car-fields';
import useResponsiblePerson from './hooks/use-responsible-person';
import useCarModelFields from './hooks/use-car-model-fields';
import { getRequestFormData, getRequestItem } from '../../utils/get-response-data';
import { GenerateFormFieldReturn } from '../../utils/form/generate-form-fields/generate-form-fields.props';
import useCheckDriversCars from './hooks/use-check-drivers-cars';
import MaxConfigurationsHint from './max-configurations-hint';

export const CustomInput = ({ value, onClick, id }: CustomInputProps) => (
  <button id={id} className={classes.custom_input} onClick={onClick}>
    {value ? (
      value.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    ) : (
      <Text color='grey500'>Monat auswählen</Text>
    )}
  </button>
);

export const ControlledInput = ({
  children,
  onButtonClick,
  className,
  prefixId = '',
  style = {}
}: ControlledInputProps) => {
  return (
    <div style={style} className={className ? `${classes.controlled_input} ${className}` : classes.controlled_input}>
      <div
        id={prefixId + 'Subtraction'}
        onClick={() => onButtonClick('subtraction')}
        className={`${classes.button} ${classes.subtraction}`}
      >
        -
      </div>
      {children}
      <div
        id={prefixId + 'Addition'}
        onClick={() => onButtonClick('addition')}
        className={`${classes.button} ${classes.addition}`}
      >
        +
      </div>
    </div>
  );
};

export const MonthsControl = ({ value, onChange, disabled }: MonthsControlProps) => {
  const monthsValues = [12, 24, 30, 36, 42, 48, 60];

  const getClassName = (monthsValue: number) => {
    let className = `${classes.value_container}`;
    if (monthsValue === value) {
      className += disabled ? ` ${classes.disabled}` : ` ${classes.active} pointer`;
    } else {
      className += disabled ? '' : ' pointer';
    }

    return className;
  };

  const clickHandler = (monthsValue: number) => {
    if (monthsValue !== value && !disabled) {
      onChange(monthsValue);
    }
  };

  return (
    <div className={classes.months_control}>
      {monthsValues.map(monthsValue => (
        <div
          id={'MonthsValue' + monthsValue}
          key={monthsValue}
          onClick={() => clickHandler(monthsValue)}
          className={getClassName(monthsValue)}
        >
          {monthsValue}
        </div>
      ))}
    </div>
  );
};

const carBrandFields = [
  generateFormField.lookup({
    label: 'Hersteller',
    name: ConfigurationFromPdfFieldNames.CarBrand,
    source: FormSourceEnum.GetCarBrands,
    validation: {
      required: errorMessages.requiredOptionSet
    }
  })
];

const restCarFields = [
  generateFormField.text({
    label: 'Variante',
    name: ConfigurationFromPdfFieldNames.Variant,
    placeholder: 'z.B. 35 TDI Sportback S tronic KOMBI 5-türig 7-Gang',
    validation: {
      required: errorMessages.required
    }
  }),
  generateFormField.optionSet({
    label: 'Kraftstoff',
    name: ConfigurationFromPdfFieldNames.FuelType,
    crmFieldType: CrmDataTypes.Picklist,
    options: [
      { value: uds_enginetypecode.benzin.toString(), label: 'Benzin' },
      { value: uds_enginetypecode.diesel.toString(), label: 'Diesel' },
      { value: uds_enginetypecode.hybrid_benzin.toString(), label: 'Hybrid/Benzin' },
      { value: uds_enginetypecode.hybrid_diesel.toString(), label: 'Hybrid/Diesel' },
      { value: uds_enginetypecode.elektro.toString(), label: 'Elektro' }
    ],
    validation: {
      required: errorMessages.requiredOptionSet
    }
  })
];

const ConfigurationFromPdf = ({ createVehicleOffer, closeAlert, refreshList }: ConfigurationFromPdfProps) => {
  const user = useTypedSelector<User>(state => state['app'].user);
  const [months, setMonths] = useState<MonthsData>({
    name: 'uds_wunschlaufzeitcode',
    crmFieldType: crmDataTypes.Picklist,
    valueFromCompany: false,
    value: 36
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isPoolcar, setIsPoolcar] = useState(false);
  const [isReplacementCar, setIsReplacementCar] = useState(false);

  const location = useLocation();
  const isMitarbeiterkonfigurationenPage = location.pathname.includes(PAGES.EMPLOYEE_CONFIGURATIONS);
  const isDriverConfigurationsPage =
    location.pathname.includes(PAGES.CONFIGURATIONS) && !location.pathname.includes(PAGES.NEW_CONFIGURATIONS);
  const [distance, setDistance] = useState<IDistance>({
    name: 'uds_laufleistungkm',
    crmFieldType: crmDataTypes.Integer,
    value: null
  });
  const firstDayCurrentMonth = useMemo(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, []);
  const twoYearsFromNow = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 2);
    return date;
  }, []);
  const [deliveryDate, setDeliveryDate] = useState<DeliveryDateData>({
    name: 'uds_wunschlieferdatum',
    crmFieldType: crmDataTypes.DateTime,
    value: null
  });
  const [formErrors, setFormErrors] = useState(false);
  const forceUpdate = useForceUpdate();

  const reactHookFormData = useForm();

  const { handleSubmit } = reactHookFormData;

  const { driverFields, setShowMaxConfigurationsHint, showMaxConfigurationsHint } = useDriverFields(
    isPoolcar,
    reactHookFormData
  );
  const { oldCarFields } = useOldCarFields(isReplacementCar, reactHookFormData);
  const { responsiblePersonFields } = useResponsiblePerson(isPoolcar, isReplacementCar, reactHookFormData);
  const { carModelFields } = useCarModelFields(reactHookFormData);
  const isPoolManager = useIsUserHaveRole(USER_ROLES.poolManager.id);
  const { checkDriversCars } = useCheckDriversCars(refreshList);

  const isDistanceError =
    (distance?.value !== null && ((distance.value || 0) < MIN_DISTANCE || (distance.value || 0) > MAX_DISTANCE)) ||
    (formErrors && !distance?.value);

  const onDistanceButtonClick = (type: ActionType) => {
    let newDistance = null;
    switch (type) {
      case 'subtraction':
        newDistance = (distance.value || 0) - 1000;
        if (newDistance >= 0) {
          setDistance({
            ...distance,
            value: newDistance
          });
        }
        break;
      case 'addition':
        newDistance = distance.value ? distance.value + 1000 : 10000;
        setDistance({
          ...distance,
          value: newDistance
        });
        break;
      default:
        break;
    }
  };

  const onDropAccepted = (files: File[]) => {
    setFiles(files);
  };

  const distanceChange = (value: AnyObject) => {
    const newDistance =
      value.floatValue >= MIN_DISTANCE ? Math.round(value.floatValue / 1000) * 1000 : value.floatValue;

    setDistance({
      ...distance,
      value: newDistance
    });
    if (distance.value === newDistance) {
      forceUpdate();
    }
  };

  const submit = (saveType: SaveTypes) => {
    if (!files.length || isDistanceError || !deliveryDate.value) {
      setFormErrors(true);
    }
    handleSubmit(data => {
      if (!files.length || isDistanceError || !deliveryDate.value) {
        return;
      }
      const fieldsForRequestAttributes = [
        ...driverFields,
        ...(oldCarFields as GenerateFormFieldReturn[]),
        ...(responsiblePersonFields as GenerateFormFieldReturn[]),
        ...carBrandFields,
        ...carModelFields,
        ...restCarFields
      ];
      const dataToSend = {
        entityName: 'uds_fahrzeugangebote',
        driverId: undefined,
        uds_baskettypecode: BASKET_TYPES.personal.value,
        id: null,
        isRequestOffer: false,
        attributes: getRequestFormData(data, fieldsForRequestAttributes) as AnyObject
      };

      [months, distance, deliveryDate].forEach(field => {
        dataToSend.attributes[field.name] = getRequestItem(field.value, field.crmFieldType);
      });

      if (dataToSend.attributes[ConfigurationFromPdfFieldNames.Driver]?.attributeValue?.id) {
        dataToSend[ConfigurationFromPdfFieldNames.Driver] =
          dataToSend.attributes[ConfigurationFromPdfFieldNames.Driver].attributeValue.id;
      }
      delete dataToSend.attributes[ConfigurationFromPdfFieldNames.Driver];

      if (!dataToSend.attributes[ConfigurationFromPdfFieldNames.OldCar]?.attributeValue) {
        delete dataToSend.attributes[ConfigurationFromPdfFieldNames.OldCar];
      }

      dataToSend.attributes['uds_zuordnung'] = getRequestItem(
        isPoolcar ? VEHICLE_OFFER_ASSIGNMENT.poolCar.value : VEHICLE_OFFER_ASSIGNMENT.employeeAssigned.value,
        crmDataTypes.Picklist
      );

      dataToSend.attributes['uds_angebotsstatuscode'] = getRequestItem('100000000', crmDataTypes.Picklist);

      dataToSend.attributes['new_status_anfrage'] = getRequestItem('100000004', crmDataTypes.Picklist);
      dataToSend.attributes['uds_configurationtypecode'] = getRequestItem(
        CONFIGURATION_TYPES.personalConfiguration.value,
        crmDataTypes.Picklist
      );

      if (isPoolManager && !dataToSend.attributes[ConfigurationFromPdfFieldNames.ResponsiblePerson]?.attributeValue) {
        dataToSend.attributes[ConfigurationFromPdfFieldNames.ResponsiblePerson] = getRequestItem(
          user.id,
          crmDataTypes.Lookup
        );
      }

      dataToSend['uds_baskettypecode'] = isPoolcar ? BASKET_TYPES.poolcar.value : BASKET_TYPES.personal.value;
      dataToSend['isRequestOffer'] = saveType === SaveTypes.RequestLeasingOffer;

      if (isPoolcar || isDriverConfigurationsPage) {
        createVehicleOffer(dataToSend, files, saveType === SaveTypes.RequestLeasingOffer);
      } else {
        checkDriversCars({
          driverId: dataToSend[ConfigurationFromPdfFieldNames.Driver] || user.id,
          successCallback: () => {
            createVehicleOffer(dataToSend, files, saveType === SaveTypes.RequestLeasingOffer);
          },
          setMaxConfigurationsNumberReached: () => setShowMaxConfigurationsHint(true)
        });
      }
    })();
  };

  return (
    <div className={classes.configuration_from_pdf}>
      <Flex justify='space-between' align='flex-start' bottom='25'>
        <Text size='20' bold color='grey800' right='10'>
          Lade die PDF deiner Herstellerkonfiguration hoch und frage den Fahrzeugpreis an.
        </Text>

        <IconHover onClick={closeAlert}>
          <CloseIcon width={15} height={15} />
        </IconHover>
      </Flex>

      <StyledConfigurationFromPdf>
        <StyledLeftContainer>
          {isMitarbeiterkonfigurationenPage && (
            <>
              <FormHook reactHookFormData={reactHookFormData} formFields={driverFields} />

              <Flex align='center' margin='20px 0'>
                <Switch
                  id='IsPoolcarSwitcher'
                  offColor={'#bfbfbf'}
                  onColor={'#335566'}
                  height={20}
                  width={40}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  onChange={event => setIsPoolcar(event)}
                  checked={isPoolcar}
                />
                <Text left='10' color='black' pointer onClick={() => setIsPoolcar(flag => !flag)}>
                  Poolcar
                </Text>
              </Flex>
            </>
          )}

          <FormHook reactHookFormData={reactHookFormData} formFields={oldCarFields} />

          <Flex align='center' margin='20px 0'>
            <Switch
              id='IsReplacementCarSwitcher'
              offColor={'#bfbfbf'}
              onColor={'#335566'}
              height={20}
              width={40}
              uncheckedIcon={false}
              checkedIcon={false}
              onChange={event => setIsReplacementCar(event)}
              checked={isReplacementCar}
            />
            <Text left='10' color='black' pointer onClick={() => setIsReplacementCar(flag => !flag)}>
              Dies ist kein Ersatzfahrzeug
            </Text>
          </Flex>

          {isMitarbeiterkonfigurationenPage && !isPoolManager && (
            <FormHook reactHookFormData={reactHookFormData} formFields={responsiblePersonFields} />
          )}

          <p className={classes.field_title}>
            Laufzeit (Monate) <span className={classes.required_mark}>*</span>
          </p>
          <MonthsComponent months={months} setMonths={setMonths} />
          <div className='mt-15' />
          {months.valueFromCompany && (
            <InfoHint type={InfoHintType.Info}>
              Die Laufzeit wird von deinem Fuhrparkleiter festgelegt und kann nicht geändert werden.
            </InfoHint>
          )}
          <div className='mt-15' />
          <p className={classes.field_title}>
            Jährliche Fahrleistung (in km/Jahr) <span className={classes.required_mark}>*</span>
          </p>
          <ControlledInput
            onButtonClick={onDistanceButtonClick}
            prefixId='Distance'
            className={isDistanceError ? classes.error : null}
          >
            <NumberFormat
              id='DistanceInput'
              value={distance.value}
              displayType='input'
              thousandSeparator='.'
              placeholder='Wert angeben (min. 10.000) oder mit +/- auswählen'
              decimalScale={0}
              decimalSeparator=','
              fixedDecimalScale={true}
              className={classes.custom_input}
              onValueChange={distanceChange}
            />
          </ControlledInput>
          {isDistanceError && (
            <div className={classes.error_message}>
              <span className='d-flex a-i-center'>
                <CircleExclamationMark color={'#BF0040'} width={'12'} height={'12'} />
                &nbsp;Der Wert sollte zwischen {MIN_DISTANCE.toLocaleString('de-DE')} und{' '}
                {MAX_DISTANCE.toLocaleString('de-DE')}
              </span>
            </div>
          )}
          <div className='mt-15' />
          <p className={classes.field_title}>
            Wunschlieferdatum <span className={classes.required_mark}>*</span>
          </p>
          <DeliveryDateComponent
            deliveryDate={deliveryDate}
            setDeliveryDate={setDeliveryDate}
            minDate={firstDayCurrentMonth}
            maxDate={twoYearsFromNow}
            formErrors={formErrors}
          />
        </StyledLeftContainer>

        <Divider vertical color='lightBlue' margin='0 35px' />

        <StyledRightContainer>
          <UploadFile
            onDropAccepted={onDropAccepted}
            accept={['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']}
            acceptString={'pdf, png, jpg, jpeg'}
            required={true}
            multiple={false}
            missingFilesError={formErrors && !files.length}
          />

          <FormHook reactHookFormData={reactHookFormData} formFields={carBrandFields} />
          <FormHook reactHookFormData={reactHookFormData} formFields={carModelFields} />
          <FormHook reactHookFormData={reactHookFormData} formFields={restCarFields} />

          {showMaxConfigurationsHint && <MaxConfigurationsHint />}
        </StyledRightContainer>
      </StyledConfigurationFromPdf>

      <Flex justify='flex-end' top='30'>
        <Button
          disabled={showMaxConfigurationsHint}
          id='SaveConfigurationButton'
          onClick={() => submit(SaveTypes.SaveForLater)}
          secondary
        >
          Für später speichern
        </Button>
        <Button
          left='10'
          disabled={
            !months.value ||
            (distance.value || 0) < MIN_DISTANCE ||
            (distance.value || 0) > MAX_DISTANCE ||
            !deliveryDate.value ||
            showMaxConfigurationsHint
          }
          id='SaveExtendedConfigurationButton'
          onClick={() => submit(SaveTypes.RequestLeasingOffer)}
        >
          Leasingangebot anfragen
        </Button>
      </Flex>
    </div>
  );
};

export default ConfigurationFromPdf;
